/** Screen 2: paste vocabulary as text, with a live preview of the parsed pairs. */
import { el, page, setChildren } from '../dom';
import { navigate } from '../router';
import { formatImport, parseImport, type ParseResult } from '../../core/parser';
import { makeSetId } from '../../core/ids';
import { getSet, upsertSet } from '../../core/storage';
import { SAMPLE_TEXT } from '../../core/sample';
import { loadCatalog, loadList, type LibraryEntry } from '../../core/library';

const PLACEHOLDER = `DE;FR
==Lernziel 01
das Haus;la maison
der Hund;le chien
==Lernziel 02
das Auto;la voiture`;

export function importScreen(context: { params: string[] }): HTMLElement {
  const editId = context.params[0] ?? null;
  const existing = editId ? getSet(editId) : null;

  const nameInput = el('input', {
    class: 'input',
    type: 'text',
    id: 'set-name',
    placeholder: 'z.B. Französisch Unit 3',
    value: existing ? existing.name : '',
    autocapitalize: 'sentences',
  }) as HTMLInputElement;

  const textarea = el('textarea', {
    class: 'input textarea',
    id: 'set-text',
    rows: 12,
    placeholder: PLACEHOLDER,
    spellcheck: 'false',
    autocapitalize: 'off',
    autocorrect: 'off',
  }) as HTMLTextAreaElement;

  if (existing) {
    textarea.value = formatImport(existing);
  }

  const preview = el('div', { class: 'preview' });
  const libraryCard = el('div', { class: 'card', hidden: true });
  const saveButton = el('button', {
    class: 'btn btn-primary btn-big',
    type: 'button',
    text: existing ? 'Änderungen speichern' : 'Set speichern',
    disabled: true,
  }) as HTMLButtonElement;

  let result: ParseResult | null = null;

  function renderPreview(): void {
    const text = textarea.value.trim();

    if (text === '') {
      result = null;
      saveButton.disabled = true;
      preview.replaceChildren(
        el('p', {
          class: 'hint',
          text: 'Erste Zeile: die zwei Sprachen. Danach pro Zeile ein Wortpaar. Mit "==Lernziel 01" teilst du das Set in Lernziele.',
        }),
      );
      return;
    }

    result = parseImport(textarea.value);
    saveButton.disabled = result.cards.length === 0;

    // One line per learning goal instead of a long word list: with several
    // goals the structure matters more than the individual pairs.
    const goals: Array<{ name: string; count: number }> = [];
    for (const card of result.cards) {
      const last = goals[goals.length - 1];
      if (last && last.name === card.group) last.count += 1;
      else goals.push({ name: card.group, count: 1 });
    }
    const hasNamedGoals = goals.some((goal) => goal.name !== '');

    const rows = hasNamedGoals
      ? goals.map((goal) =>
          el('li', { class: 'preview-row' }, [
            el('span', { class: 'preview-native', text: goal.name || 'Ohne Lernziel' }),
            el('span', { class: 'preview-foreign', text: `${goal.count} Wörter` }),
          ]),
        )
      : result.cards.slice(0, 8).map((card) =>
          el('li', { class: 'preview-row' }, [
            el('span', { class: 'preview-native', text: card.native }),
            el('span', { class: 'preview-arrow', text: '→' }),
            el('span', { class: 'preview-foreign', text: card.foreign }),
          ]),
        );

    setChildren(preview, [
      el('p', { class: 'preview-summary' }, [
        el('strong', { text: `${result.cards.length} Wortpaare` }),
        el('span', { text: ` · ${result.nativeLang} → ${result.foreignLang}` }),
        hasNamedGoals ? el('span', { text: ` · ${goals.length} Lernziele` }) : null,
      ]),
      el('ul', { class: 'preview-list' }, rows),
      !hasNamedGoals && result.cards.length > 8
        ? el('p', { class: 'hint', text: `… und ${result.cards.length - 8} weitere` })
        : null,
      result.issues.length > 0
        ? el('details', { class: 'issues' }, [
            el('summary', { text: `${result.issues.length} Hinweis(e)` }),
            el(
              'ul',
              {},
              result.issues.map((issue) =>
                el('li', { text: `Zeile ${issue.line}: ${issue.reason}` }),
              ),
            ),
          ])
        : null,
    ]);
  }

  textarea.addEventListener('input', renderPreview);

  /**
   * Lists the word lists the server provides. The catalogue is optional, so
   * the card stays hidden when no library folder is mounted.
   */
  function renderLibrary(entries: LibraryEntry[]): void {
    const status = el('p', { class: 'hint', text: 'Tippe auf eine Liste, um sie zu übernehmen.' });

    async function useEntry(entry: LibraryEntry, button: HTMLButtonElement): Promise<void> {
      button.disabled = true;
      status.textContent = `${entry.name} wird geladen …`;
      try {
        const text = await loadList(entry.file);
        textarea.value = text;
        // Only fill the name when the user has not typed one, so editing a set
        // never loses its title.
        if (nameInput.value.trim() === '') nameInput.value = entry.name;
        renderPreview();
        status.textContent = `${entry.name} übernommen. Unten prüfen und speichern.`;
      } catch (error) {
        status.textContent = `${entry.name} konnte nicht geladen werden: ${
          error instanceof Error ? error.message : 'unbekannter Fehler'
        }`;
      } finally {
        button.disabled = false;
      }
    }

    setChildren(libraryCard, [
      el('h2', { class: 'section-title', text: 'Fertige Listen' }),
      el(
        'ul',
        { class: 'library-list' },
        entries.map((entry) => {
          const button = el('button', {
            class: 'btn btn-ghost',
            type: 'button',
            text: 'Übernehmen',
          }) as HTMLButtonElement;
          button.addEventListener('click', () => {
            void useEntry(entry, button);
          });

          return el('li', { class: 'library-row' }, [
            el('span', { class: 'library-name', text: entry.name }),
            button,
          ]);
        }),
      ),
      status,
    ]);

    libraryCard.hidden = false;
  }

  void loadCatalog().then((entries) => {
    if (entries.length > 0) renderLibrary(entries);
  });

  saveButton.addEventListener('click', () => {
    if (!result || result.cards.length === 0) return;

    const name =
      nameInput.value.trim() ||
      `${result.nativeLang} → ${result.foreignLang} (${result.cards.length} Wörter)`;

    if (existing) {
      // Keep the progress of every word pair that still exists.
      const previous = new Map(existing.cards.map((card) => [card.id, card]));
      upsertSet({
        ...existing,
        name,
        nativeLang: result.nativeLang,
        foreignLang: result.foreignLang,
        cards: result.cards.map((card) => previous.get(card.id) ?? card),
      });
    } else {
      const now = Date.now();
      upsertSet({
        id: makeSetId(),
        name,
        nativeLang: result.nativeLang,
        foreignLang: result.foreignLang,
        createdAt: now,
        updatedAt: now,
        cards: result.cards,
      });
    }

    navigate('/sets');
  });

  const view = page({
    title: existing ? 'Set bearbeiten' : 'Wörter einfügen',
    back: '/sets',
    children: [
      libraryCard,
      el('div', { class: 'card' }, [
        el('label', { class: 'label', for: 'set-name', text: 'Name des Sets' }),
        nameInput,
        el('label', { class: 'label', for: 'set-text', text: 'Wörterliste' }),
        textarea,
        el('p', {
          class: 'hint',
          text: 'Eine Zeile "==Lernziel 01" beginnt ein neues Lernziel. Alles darunter gehört dazu, bis das nächste "==" kommt.',
        }),
        el('div', { class: 'row' }, [
          el('button', {
            class: 'btn btn-ghost',
            type: 'button',
            text: 'Beispiel einfügen',
            onclick: () => {
              textarea.value = SAMPLE_TEXT;
              renderPreview();
            },
          }),
          el('button', {
            class: 'btn btn-ghost',
            type: 'button',
            text: 'Leeren',
            onclick: () => {
              textarea.value = '';
              renderPreview();
            },
          }),
        ]),
      ]),
      el('div', { class: 'card' }, [preview]),
      el('div', { class: 'bottom-actions' }, [saveButton]),
    ],
  });

  renderPreview();
  return view;
}
